
import { supabase } from '@/integrations/supabase/client';
import type { SelectedCriterion, DynamicRatingData } from '@/types/evaluationCriteria';
import type { Referent, Campaign } from '@/types/unified';
import { markReferentAccessed } from './referentStatusService';
import { validateContextAndExecute } from '@/lib/contextAwareService';
import { logger } from '@/utils/productionLogger';
import { cacheHelpers } from './performanceCache';

export const loadReferentFormData = async (
  token: string | null
): Promise<{
  referent: Referent;
  campaign: Campaign;
  evaluationCriteria: SelectedCriterion[];
  initialRatings: DynamicRatingData;
}> => {
  if (!token) {
    throw new Error('Token manquant dans l\'URL');
  }

  logger.debug('📡 Chargement des données référent avec cache', { hasToken: !!token });

  // TEMPORAIRE: Vider le cache pour forcer le rechargement avec la correction des critères
  cacheHelpers.invalidateFormData(token);

  // OPTIMISATION: Vérifier le cache d'abord
  const cachedData = cacheHelpers.getReferentData<{
    referent: Referent;
    campaign: Campaign;
    evaluationCriteria: SelectedCriterion[];
    initialRatings: DynamicRatingData;
  }>(token);

  if (cachedData) {
    logger.debug('🚀 Données référent récupérées depuis le cache');
    return cachedData;
  }

  // Charger le référent via RPC sécurisée avec validation du contexte
  const referentRow = await validateContextAndExecute(async () => {
    const { data, error } = await supabase
      .rpc('get_referent_form_data', { referent_id_param: token })
      .single();

    if (error) {
      logger.error('❌ Erreur chargement référent (RPC)', error);
      
      // Messages d'erreur plus spécifiques
      if (error && typeof error === 'object' && 'code' in error && error.code === 'PGRST116') {
        throw new Error('Référent introuvable. Le lien pourrait être invalide ou expiré.');
      }
      
      throw error;
    }

    return data;
  }, 'referent');

  if (!referentRow) {
    throw new Error('Aucune donnée trouvée pour ce référent');
  }

  // Les données de campagne sont maintenant incluses dans la réponse RPC
  const campaignData = {
    id: referentRow.campaign_id,
    candidate_name: referentRow.campaign_candidate_name,
    target_position: referentRow.campaign_target_position,
    selected_evaluation_criteria: referentRow.campaign_selected_evaluation_criteria,
    ai_extracted_data: referentRow.campaign_ai_extracted_data
  };

  // Reconstituer la structure attendue par le code existant avec les nouvelles colonnes de dates
  const referentData = { 
    ...referentRow, 
    campaigns: campaignData
  } as unknown as Referent & { 
    campaigns: Campaign;
  };

  logger.info('✅ Référent trouvé', {
    referentId: referentData.id,
    referentName: referentData.name,
    campaignId: referentData.campaign_id
  });

  // Marquer le référent comme ayant accédé au formulaire (passe de "Invité" à "A répondu")
  try {
    await markReferentAccessed(referentData.id);
    logger.info('✅ Référent marqué comme ayant accédé au formulaire');
  } catch (error) {
    logger.warn('⚠️ Erreur lors du marquage d\'accès (non bloquant)', error as Error);
    // Cette erreur n'est pas bloquante pour le chargement du formulaire
  }

  // Charger les critères d'évaluation de la campagne
  const criteriaData = campaignData.selected_evaluation_criteria;
  let criteria: SelectedCriterion[] = [];
  
  if (Array.isArray(criteriaData)) {
    criteria = (criteriaData as unknown[]).filter(
      (item): item is SelectedCriterion => 
        typeof item === 'object' && 
        item !== null && 
        'id' in item && 
        'name' in item && 
        'description' in item && 
        'category' in item
    );
  } else if (criteriaData && typeof criteriaData === 'object') {
    const values = Object.values(criteriaData as Record<string, unknown>);
    criteria = values.filter(
      (item): item is SelectedCriterion => 
        typeof item === 'object' && 
        item !== null && 
        'id' in item && 
        'name' in item && 
        'description' in item && 
        'category' in item
    );
  }

  // Initialiser les ratings avec une valeur par défaut de 5 pour chaque critère
  const initialRatings: DynamicRatingData = {};
  criteria.forEach((criterion: SelectedCriterion) => {
    initialRatings[criterion.id] = 5;
  });

  logger.info('✅ Critères d\'évaluation chargés', { count: criteria.length });

  const result = {
    referent: referentData,
    campaign: referentData.campaigns,
    evaluationCriteria: criteria,
    initialRatings
  };

  // OPTIMISATION: Mettre en cache pour les prochaines visites
  cacheHelpers.setReferentData(token, result);
  logger.debug('💾 Données référent mises en cache');

  return result;
};
